// Legacy tool for draft mode
import { Edge, Node } from "@xyflow/react";
import { atom } from "jotai";
import { parseCanvasDataFromZSL } from "@zapier/streaming";
import { z } from "zod";
/**
 * TEMPORARY TYPE DECLARATION
 * This is a temporary solution to fix type errors with @zapier/zsl-creator-schemas
 * This should be replaced with proper type declarations from the package in the future
 */
// @ts-ignore
import { ZslSchemaForApi } from "@zapier/zsl-creator-schemas";
import {
  canvasAtom,
  edgesAtom,
  nodesAtom,
  zslAtom,
} from "@zapier/canvas-state";

import { JotaiTool } from "../../index";

const EDITABLE_ASSETS = "table, form, agents, and chatbot";

export const editSystemAtom = atom<JotaiTool<z.infer<typeof ZslSchemaForApi>>>(
  () => ({
    name: "edit_system",
    description: `This tool is used to:
      - Edit ${EDITABLE_ASSETS} assets.
      - Add new ${EDITABLE_ASSETS}s.
      - Remove any assets.

      ## Important
      This tool should not be used to:
      - Edit or add zaps.
      - Existing zaps MUST be preserved unchanged in your response.

      ## Important
      If a user asks to create or edit an agent in such a way that is not possible according to the schema, apologize concisely and suggest that they use the Agent Builder.
      If the user asks to add a 3rd party app to an agent, suggest they use the Agent Builder.

      When calling this tool, make sure to provide the entire new system including:
      1. Your edits to ${EDITABLE_ASSETS}s
      2. All existing zaps exactly as they are (do not modify or omit them)
      3. Any other existing components that should be preserved

      Your response must contain the complete system state, not just the changed parts.
      If a requested change cannot be made because of schema constraints, explain this politely but concisely. Do not offer excessive detail.
      `,
    parameters: ZslSchemaForApi,
    execute: atom(
      null,
      async (get, set, { args: editedZsl, reactFlowInstance }) => {
        let validatedZsl: z.infer<typeof ZslSchemaForApi>;
        try {
          validatedZsl = ZslSchemaForApi.parse(editedZsl);
        } catch (error) {
          return {
            $error: {
              message: `Error: Invalid ZSL data provided. ${error instanceof Error ? error.message : "Unknown validation error."}`,
            },
          };
        }

        const canvas = get(canvasAtom);
        const nodes = get(nodesAtom);
        const edges = get(edgesAtom);

        set(zslAtom, validatedZsl);
        const nodesAndEdges = parseCanvasDataFromZSL(validatedZsl, {
          canvas: {
            name: canvas.name || "Untitled Canvas",
            description: canvas.description || "",
            nodes: nodes as any,
            edges: edges as any,
          },
          warnings: [],
        });
        if (nodesAndEdges) {
          const {
            canvas: { nodes: updatedNodes, edges: updatedEdges },
          } = nodesAndEdges;
          set(nodesAtom, updatedNodes as Node[]);
          set(edgesAtom, updatedEdges as Edge[]);

          if (updatedNodes.length > nodes.length) {
            // Delay fitView to ensure DOM updates complete before adjusting view
            setTimeout(() => {
              reactFlowInstance.fitView();
            }, 100);
          }
        }
        return {
          content: [{ type: "text", text: "Canvas updated" }],
        };
      },
    ),
  }),
);
editSystemAtom.debugLabel = "editSystemAtom";
